import {
  Box,
  CardMedia,
  Grid,
  Typography,
  Skeleton,
  Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import color from "../../components/utils/Colors";
import CourseChapterCard from "../../components/cards/CourseChapterCard";
import { useNavigate, useParams } from "react-router-dom";
import { getOneDataFromTable, getDataFromTable } from "../../services/services";

interface Lesson {
  title: string;
  lessonNumber: string;
  lessonTime: string;
}

interface Chapter {
  courseNumber: string;
  courseTitle: string;
  lessons: Lesson[];
}

interface CourseData {
  title: string;
  description: string;
  level: string;
  duration: string;
  chapters: Chapter[];
  coverImage?: string;
  shortVideo?: string;
}

/**
 * Maps raw API course-content records into the Chapter/Lesson shape
 * expected by the UI. The API returns courseContent rows with:
 *   id, courseId, lectureName, lectureDesc, lectureOrder, contentType, status
 */
function mapCourseContent(rawContents: any[]): Chapter[] {
  if (!rawContents || rawContents.length === 0) return [];

  // Group by lectureOrder (treat each content item as its own "chapter")
  const chapters: Chapter[] = rawContents.map((item: any, idx: number) => ({
    courseNumber: String(item.lectureOrder ?? idx + 1).padStart(2, "0"),
    courseTitle: item.lectureName ?? `Chapter ${idx + 1}`,
    lessons: item.lectureDesc
      ? [
          {
            title: item.lectureDesc,
            lessonNumber: `Lesson 1`,
            lessonTime: item.contentType ?? "–",
          },
        ]
      : [],
  }));

  return chapters;
}

const Course: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState<CourseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseId) return;

    setLoading(true);
    setError(null);

    // Fetch the course record and its content in parallel
    Promise.allSettled([
      getOneDataFromTable("course", courseId),
      getDataFromTable("courseContent", { courseId }),
    ])
      .then(([courseResult, contentResult]) => {
        if (courseResult.status === "rejected") {
          setError("Failed to load course. Please try again.");
          return;
        }

        const course = (courseResult.value as any)?.data?.data ?? {};
        const rawContents: any[] =
          contentResult.status === "fulfilled"
            ? ((contentResult.value as any)?.data?.data ?? [])
            : [];

        setCourseData({
          title: course.title ?? course.courseName ?? "Course",
          description:
            course.description ?? course.subTitle ?? course.whoCanJoin ?? "",
          level: course.level ?? "",
          duration: course.timeline ?? course.duration ?? "",
          chapters: mapCourseContent(rawContents),
          coverImage: process.env.REACT_APP_PUBLIC_BASE_URL+course.coverImage,
          shortVideo: process.env.REACT_APP_PUBLIC_BASE_URL+course.shortVideo,
        });
      })
      .catch(() => {
        setError("Failed to load course data. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [courseId]);

  if (error) {  
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: "10px" }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          p: 4,
          mt: 2,
          pt: 0,
          background: color.secondColor,
          border: "solid 1px",
          borderColor: color.borderColor,
        }}
      >
        {loading ? (
          <>
            <Skeleton variant="text" width="60%" height={40} sx={{ mt: 2 }} />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
          </>
        ) : (
          <>
            <Typography
              onClick={() => navigate(`/open-course/${courseId}`)}
              sx={{
                color: color.textColor,
                fontWeight: "600",
                fontSize: { xs: "18px", sm: "20px", md: "24px" },
                padding: "20px",
                pl: 0,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {courseData?.title}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
              {courseData?.level && (
                <Chip
                  label={courseData.level}
                  size="small"
                  sx={{ background: color.firstColor, color: "white" }}
                />
              )}
              {courseData?.duration && (
                <Chip
                  label={courseData.duration}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: color.firstColor, color: color.textColor }}
                />
              )}
            </Box>

            {courseData?.description && (
              <Typography>{courseData.description}</Typography>
            )}
          </>
        )}
      </Box>

      {/* Video/Thumbnail Section */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          my: 4,
          mt: 8,
          height: { xs: "300px", sm: "400px", md: "600px" },
          background: "#808080ba",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {courseData?.shortVideo ? (
          <CardMedia
            component="video"
            controls
            src={courseData.shortVideo}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <CardMedia
            component="img"
            image={courseData?.coverImage}
            alt="Course Video"
            sx={{
              backgroundColor: "#D9D9D9",
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
      </Box>

      {/* Chapters Section */}
      <Box>
        {loading ? (
          <Grid
            container
            spacing={2}
            sx={{ p: 8, justifyContent: "space-between" }}
          >
            {[1, 2].map((i) => (
              <Grid item md={5} xs={12} key={i}>
                <Skeleton variant="rounded" height={250} />
              </Grid>
            ))}
          </Grid>
        ) : courseData?.chapters && courseData.chapters.length > 0 ? (
          <Grid
            container
            spacing={2}
            sx={{ p: 8, justifyContent: "space-between" }}
          >
            {courseData.chapters.reverse().map((chapter, index) => (
              <Grid item md={5} xs={12} key={index}>
                <CourseChapterCard
                  courseNumber={chapter.courseNumber}
                  courseTitle={chapter.courseTitle}
                  lessons={chapter.lessons}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          !loading && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography sx={{ color: "gray" }}>
                No course content available yet.
              </Typography>
            </Box>
          )
        )}
      </Box>
    </div>
  );
};

export default Course;
